import { Certificate, Media, User } from "../../../types/types";
import { UserRoleEnum } from "../../User/enums";

import { Bucket, ComplianceEnum } from "../enum/jobEnums";
import { CandidateAssessedVia, ReviewCriteriaKey } from "../enum/reviewEnums";

export type CandidateDetails = {
  user: User;
  fitScore: number;
  fitScoreJustification: string;
  candidateStatus: Bucket;
  candidateOverview: { key: string; value: string }[];
  languagesSpoken: string[];
  willingnessToRelocate: boolean;
  introductionVideo: Media;
  clubsAcademiesWorkedIn: string[];
  certificateAndLicenses: Certificate[];
};

export type GinoFitScoreData = {
  candidateDetails: CandidateDetails;
  jobName: string;
  summary: string;
  strengths: string[];
  gaps: string[];
  recommendation: string;
  requiredSubmission: {
    title: string;
    jobRequirement: string[] | boolean;
    candidateSubmitted: string[] | boolean;
    compliance: ComplianceEnum;
    fitScore: number;
  }[];
};

export type TechnicalSkills = {
  FIRST_TOUCH_RECEIVING: number;
  PASSING_RANGE_ACCURACY: number;
  DRIBBLING_1V1_ABILITY: number;
  SHOOTING_FINISHING: number;
  CROSSING_DELIVERY: number;
  HEADING_TECHNIQUE: number;
  percentage: number;
};

export type TacticalAwareness = {
  POSITIONING: number;
  OFF_THE_BALL_MOVEMENT: number;
  DEFENSIVE_AWARENESS: number;
  TRANSITION_PLAY: number;
  SPATIAL_AWARENESS: number;
  TACTICAL_ADAPTABILITY: number;
  percentage: number;
};

export type AthleticismPhysical = {
  ACCELERATION: number;
  TOP_SPEED: number;
  STRENGTH: number;
  INJURY_RESISTANCE_RECOVERY: number;
  ENDURANCE: number;
  AGILITY: number;
  FLEXIBILITY_COORDINATION: number;
  percentage: number;
};

export type MindsetPsychological = {
  CONCENTRATION: number;
  CONFIDENCE: number;
  LEADERSHIP: number;
  DISCIPLINE: number;
  WORK_RATE: number;
  RESILIENCE: number;
  COACH_ABILITY: number;
  PROFESSIONALISM: number;
  percentage: number;
};

// Type for Player report review
type PlayerReportReview = {
  TECHNICAL_SKILLS: TechnicalSkills;
  TACTICAL_AWARENESS: TacticalAwareness;
  ATHLETICISM_PHYSICAL: AthleticismPhysical;
  MINDSET_PSYCHOLOGICAL: MindsetPsychological;
};

// Type for Agent report review
type AgentReportReview = {
  BUSINESS_SKILLS: {
    negotiation: number;
    marketKnowledge: number;
    networkingAbility: number;
    clientManagement: number;
    percentage: number;
  };
  PROFESSIONAL_CONDUCT: {
    ethics: number;
    communication: number;
    reliability: number;
    transparency: number;
    percentage: number;
  };
};

// Type for Coach report review
type CoachReportReview = {
  COACHING_SKILLS: {
    tacticalKnowledge: number;
    trainingMethodology: number;
    playerDevelopment: number;
    gameManagement: number;
    percentage: number;
  };
  LEADERSHIP: {
    teamManagement: number;
    communication: number;
    decisionMaking: number;
    motivation: number;
    percentage: number;
  };
};

// Type for Scout report review
type ScoutReportReview = {
  SCOUTING_SKILLS: {
    talentIdentification: number;
    marketKnowledge: number;
    analyticalAbility: number;
    reportWriting: number;
    percentage: number;
  };
  PROFESSIONAL_EXPERTISE: {
    technicalKnowledge: number;
    networkQuality: number;
    researchAbility: number;
    evaluation: number;
    percentage: number;
  };
};

// Type for Other role report review
type OtherReportReview = {
  GENERAL_SKILLS: {
    professionalExpertise: number;
    communication: number;
    workEthic: number;
    adaptability: number;
    percentage: number;
  };
};

// Main ReportInfoData type with conditional report_review based on role
export type ReportReview =
  | PlayerReportReview
  | AgentReportReview
  | CoachReportReview
  | ScoutReportReview
  | OtherReportReview;

export type ReportInfoData = {
  reportBy: string;
  reportDate: string;
  fitScore: number;
  candidateRole: UserRoleEnum;
  jobName: string;
  report_review: ReportReview;
  candidate: User;
  ginoFitScoreData: GinoFitScoreData;
};

export interface ReviewCriteria {
  key: ReviewCriteriaKey;
  rating: number;
  note?: string;
}

export interface CandidateDetailedReview {
  id: string;
  bucket: Bucket;
  reviewerUserId: number;
  overallFitScore: number;
  assessedVia: CandidateAssessedVia;
  criteria: ReviewCriteria[];
  note: string;
  createdAt: string;
}

export interface CandidateReviewPayload {
  bucket: Bucket;
  note?: string;
  review: {
    overallFitScore: number;
    assessedVia: CandidateAssessedVia;
    criteria: ReviewCriteria[];
    ginoSummary?: string;
    tags?: string[];
    finalNarrative?: string | null;
  };
}

export type ReviewCategoryKey =
  | "PROFESSIONAL_EXPERTISE"
  | "COMMUNICATION_LEADERSHIP"
  | "PROFESSIONALISM_WORK_ETHIC"
  | "DEVELOPMENT_GROWTH"
  | "TECHNICAL_SKILLS"
  | "TACTICAL_AWARENESS"
  | "ATHLETICISM_PHYSICAL"
  | "MINDSET_PSYCHOLOGICAL";

export const REVIEW_CRITERIA_CATEGORIES: Record<ReviewCategoryKey, ReviewCriteriaKey[]> = {
  PROFESSIONAL_EXPERTISE: [
    ReviewCriteriaKey.DOMAIN_KNOWLEDGE,
    ReviewCriteriaKey.TRACK_RECORD,
    ReviewCriteriaKey.ANALYTICAL_SKILLS,
    ReviewCriteriaKey.TALENT_IDENTIFICATION,
    ReviewCriteriaKey.INNOVATION_METHODS,
  ],
  COMMUNICATION_LEADERSHIP: [
    ReviewCriteriaKey.LEADERSHIP,
    ReviewCriteriaKey.COMMUNICATION_CLARITY,
    ReviewCriteriaKey.TEAM_MANAGEMENT,
    ReviewCriteriaKey.INFLUENCE_NETWORKING,
    ReviewCriteriaKey.CULTURAL_ADAPTABILITY,
  ],
  PROFESSIONALISM_WORK_ETHIC: [
    ReviewCriteriaKey.DISCIPLINE,
    ReviewCriteriaKey.WORK_RATE,
    ReviewCriteriaKey.INTEGRITY_ETHICS,
    ReviewCriteriaKey.RESILIENCE,
    ReviewCriteriaKey.COACHABILITY,
    ReviewCriteriaKey.PROFESSIONAL_PRESENCE,
  ],
  DEVELOPMENT_GROWTH: [
    ReviewCriteriaKey.LEARNING_ORIENTATION,
    ReviewCriteriaKey.ADAPTABILITY,
    ReviewCriteriaKey.FUTURE_POTENTIAL,
    ReviewCriteriaKey.SPECIALIZATION_FIT,
  ],
  TECHNICAL_SKILLS: [
    ReviewCriteriaKey.FIRST_TOUCH_RECEIVING,
    ReviewCriteriaKey.PASSING_RANGE_ACCURACY,
    ReviewCriteriaKey.DRIBBLING_1V1,
    ReviewCriteriaKey.SHOOTING_FINISHING,
    ReviewCriteriaKey.CROSSING_DELIVERY,
    ReviewCriteriaKey.HEADING_TECHNIQUE,
  ],
  TACTICAL_AWARENESS: [
    ReviewCriteriaKey.POSITIONING,
    ReviewCriteriaKey.OFF_BALL_MOVEMENT,
    ReviewCriteriaKey.DEFENSIVE_AWARENESS,
    ReviewCriteriaKey.TRANSITION_PLAY,
    ReviewCriteriaKey.SPATIAL_AWARENESS,
    ReviewCriteriaKey.TACTICAL_ADAPTABILITY,
  ],
  ATHLETICISM_PHYSICAL: [
    ReviewCriteriaKey.ACCELERATION,
    ReviewCriteriaKey.TOP_SPEED,
    ReviewCriteriaKey.STRENGTH,
    ReviewCriteriaKey.ENDURANCE,
    ReviewCriteriaKey.AGILITY,
    ReviewCriteriaKey.FLEXIBILITY_COORDINATION,
    ReviewCriteriaKey.INJURY_RESISTANCE_RECOVERY,
  ],
  MINDSET_PSYCHOLOGICAL: [
    ReviewCriteriaKey.CONCENTRATION,
    ReviewCriteriaKey.CONFIDENCE,
    ReviewCriteriaKey.LEADERSHIP,
    ReviewCriteriaKey.DISCIPLINE,
    ReviewCriteriaKey.WORK_RATE,
    ReviewCriteriaKey.RESILIENCE,
    ReviewCriteriaKey.COACHABILITY,
    ReviewCriteriaKey.PROFESSIONALISM,
  ],
};

export const reviewCriteriaCategoriesList = (
  Object.entries(REVIEW_CRITERIA_CATEGORIES) as [ReviewCategoryKey, ReviewCriteriaKey[]][]
).map(([categoryKey, criteriaKeys]) => ({
  categoryKey,
  criteriaKeys,
}));
